import { ArticleStatus, Prisma } from '@prisma/client'
import { ExtendedStats } from '../interaction'
import { IMediaItem } from '../media/core'
import { UserItem } from '../user/user'
import { CategoryItem } from './category'


export interface ArticleCategoryItem extends CategoryItem {
  description?: string | null
  viewCount?: number
}

export interface ArticleItem {
  id: string
  createdAt: string
  updatedAt: string
  title: string
  slug: string
  body: string
  excerpt: string | null
  metaTitle: string | null
  metaDescription: string | null
  publishedAt: string | null
  status: ArticleStatus | string
  isDeleted: boolean
  viewCount: number

  // Core relations
  categoryId: string | null
  category: ArticleCategoryItem | null
  authorId?: string | null
  author?: Partial<UserItem> | null
  media: IMediaItem[]
  tags?: string[]
  keywords: string[]
  relatedArticles?: ArticleItem[]

  // Stats property for interaction stats
  stats?: ExtendedStats

  statusHistory: any[]

  myFavourite?: boolean
  
  // Additional UI properties
  isFeatured?: boolean
  isTrending?: boolean
}

// Define valid sort fields
export const ARTICLE_SORT_FIELDS = ['createdAt', 'title', 'status', 'publishedAt'] as const
export type ArticleSortField = typeof ARTICLE_SORT_FIELDS[number]

export interface ArticleOrderBy {
  field: typeof ARTICLE_SORT_FIELDS[number]
  direction: 'asc' | 'desc'
}

export const DEFAULT_ARTICLE_ORDER: ArticleOrderBy = {
  field: 'createdAt',
  direction: 'desc'
}

// Define the filter configuration type
export interface ArticleFilterConfig {
  search: {
    type: 'text'
    searchFields: Array<keyof Prisma.ArticleWhereInput>
  }
  status: {
    type: 'select'
    options: ArticleStatus[]
    multiple: true
  }
  category: {
    type: 'select'
    options: ArticleCategoryItem[]
  }
  published: {
    type: 'select'
    options: [true, false]
  }
  sortBy: {
    type: 'sort'
    options: typeof ARTICLE_SORT_FIELDS
    defaultValue: 'createdAt'
  }
  order: 'asc' | 'desc'
}

// Define the query parameters type
export interface ArticleListParams {
  skip: number
  take: number
  where: Prisma.ArticleWhereInput
  orderBy: Prisma.ArticleOrderByWithRelationInput
  include: Prisma.ArticleInclude
}

// Helper to transform filter values to query parameters
export function createArticleQueryParams(
  filters: Partial<Record<keyof ArticleFilterConfig, any>>,
  pagination: { page: number; take: number }
): ArticleListParams {
  const { search, status, category, published, sortBy, order } = filters

  return {
    skip: (pagination.page - 1) * pagination.take,
    take: pagination.take,
    where: {
      isDeleted: false,
      ...(search && {
        OR: ['title', 'content'].map((field) => ({
          [field]: { contains: search, mode: 'insensitive' }
        }))
      }),
      ...(status?.length && { status: { in: status } }),
      ...(category && { categoryId: category }),
      ...(typeof published === 'boolean' && { published })
    },
    orderBy: {
      [sortBy || 'createdAt']: order || 'desc'
    },
    include: {
      category: true,
      author: true,
      // Optionally, if you later join the Target for interactions, include it here:
    }
  }
}


